import React, { useEffect, useRef, useState } from "react";
import { Button, Typography, Divider, Tag } from "antd";

const { Text, Title } = Typography;

const CANVAS_W = 900;
const CANVAS_H = 740;
const CAM_W = 160;
const CAM_H = 120;
const PLAYER_HIT_COOLDOWN_MS = 1000;

// Motion thresholds
const MOTION_DIFF = 18;
const MIN_ACTIVE_PIXELS = 75; // Total motion threshold to activate the tracker
const JITTER_DIFFERENCE_THRESHOLD = 10; // Minimum difference in motion pixels required to switch lanes

// Difficulty settings
const BASE_OBSTACLE_SPEED = 2;
const SPEED_INCREASE_RATE = 0.01;
const SPAWN_TIME_BASE = 1200;

/* -----------------------------------------------
   MOTION RUNNER – Zone Tracking with Jitter Filter
------------------------------------------------ */
export default function StepRunner() {
  const videoRef = useRef(null);
  const camCanvasRef = useRef(null);
  const gameCanvasRef = useRef(null);

  const rafRef = useRef(null);
  const streamRef = useRef(null);
  const previousFrameRef = useRef(null);

  const runningRef = useRef(false);

  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState("Ready – Click Start");
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);

  /* 0 = left lane, 1 = center, 2 = right */
  const playerLaneRef = useRef(1); // Player's current lane (state)
  const roadOffsetRef = useRef(0);

  const gestureRef = useRef({
    leftMotionCount: 0,
    rightMotionCount: 0,
    centerMotionCount: 0,
    activeZone: 1, // 0: Left, 1: Center, 2: Right
  });

  const playerRef = useRef({
    invulnerable: false,
  });

  const gameRef = useRef({
    time: 0,
    obstacles: [],
    gameOver: false,
    currentScore: 0,
  });

  /* -------------------------------------------------------
      ZONE TRACKING (Responsive with Jitter Filter)
  ---------------------------------------------------------*/
  function detectZone(video) {
    if (!video || video.readyState < 2) return;

    const temp = document.createElement("canvas");
    temp.width = CAM_W;
    temp.height = CAM_H;

    const ctx = temp.getContext("2d");

    ctx.save();
    ctx.scale(-1, 1);
    ctx.drawImage(video, -CAM_W, 0, CAM_W, CAM_H);
    ctx.restore();

    const { data } = ctx.getImageData(0, 0, CAM_W, CAM_H);

    if (!previousFrameRef.current) {
      previousFrameRef.current = new Uint8ClampedArray(data);
      return;
    }

    const prev = previousFrameRef.current;

    let leftMotion = 0;
    let rightMotion = 0;
    let centerMotion = 0;
    let totalMotion = 0;

    const CAM_THIRD = CAM_W / 3;
    const motionCounts = [];

    // 1. Calculate Motion in Each Zone
    for (let y = 0; y < CAM_H; y++) {
      for (let x = 0; x < CAM_W; x++) {
        const i = (y * CAM_W + x) * 4;
        const diff =
          (Math.abs(data[i] - prev[i]) +
            Math.abs(data[i + 1] - prev[i + 1]) +
            Math.abs(data[i + 2] - prev[i + 2])) /
          3;

        if (diff > MOTION_DIFF) {
          totalMotion++;
          if (x < CAM_THIRD) leftMotion++;
          else if (x > 2 * CAM_THIRD) rightMotion++;
          else centerMotion++;
        }
      }
    }

    previousFrameRef.current = new Uint8ClampedArray(data);

    motionCounts[0] = leftMotion;
    motionCounts[1] = centerMotion;
    motionCounts[2] = rightMotion;

    // Update motion counts for visualization
    gestureRef.current.leftMotionCount = leftMotion;
    gestureRef.current.rightMotionCount = rightMotion;
    gestureRef.current.centerMotionCount = centerMotion;

    // 2. Determine Dominant Zone (Responsive Logic)
    let newDominantZone = playerLaneRef.current; // Assume current lane unless a clear switch occurs
    const currentLane = playerLaneRef.current;

    if (runningRef.current && totalMotion > MIN_ACTIVE_PIXELS) {
      let maxMotion = 0;
      let maxIndex = currentLane;

      // Find the lane with the absolute highest motion
      for (let i = 0; i < 3; i++) {
        if (motionCounts[i] > maxMotion) {
          maxMotion = motionCounts[i];
          maxIndex = i;
        }
      }

      // **JITTER FILTER:**
      // If the dominant zone is different from the current lane,
      // check if the difference is greater than the threshold.
      if (maxIndex !== currentLane) {
        const motionInMaxZone = motionCounts[maxIndex];
        const motionInCurrentZone = motionCounts[currentLane];

        if (
          motionInMaxZone >
          motionInCurrentZone + JITTER_DIFFERENCE_THRESHOLD
        ) {
          // The new zone is clearly dominant enough to warrant a switch
          newDominantZone = maxIndex;
        }
      }
    }

    // 3. Update Player Lane State (Instant Move)
    if (runningRef.current) {
      playerLaneRef.current = newDominantZone;
      gestureRef.current.activeZone = newDominantZone;
    }
  }

  /* ------------------------------------------------------
      GAME LOGIC – 3 lanes
  -------------------------------------------------------*/
  function spawnObstacle() {
    const lane = Math.floor(Math.random() * 3);
    gameRef.current.obstacles.push({
      lane,
      y: -60,
      speed: BASE_OBSTACLE_SPEED,
      hit: false,
    });
  }

  function step(dt) {
    const g = gameRef.current;

    // Update score
    g.currentScore += dt * 0.02;
    setScore(g.currentScore);

    roadOffsetRef.current =
      (roadOffsetRef.current + dt * 0.02 * BASE_OBSTACLE_SPEED) % 60;

    g.time += dt;

    // Spawn obstacles
    if (g.time > SPAWN_TIME_BASE + Math.random() * 600) {
      spawnObstacle();
      g.time = 0;
    }

    // Move obstacles and filter out-of-bounds
    g.obstacles = g.obstacles
      .map((o) => ({ ...o, y: o.y + o.speed }))
      .filter((o) => o.y < CANVAS_H + 80 && !o.hit);

    // Collision detection
    const playerY = CANVAS_H - 150;
    const laneW = CANVAS_W / 3;

    g.obstacles.forEach((o) => {
      if (o.hit) return;

      const obstacleX = o.lane * laneW + laneW / 2;
      const playerX = playerLaneRef.current * laneW + laneW / 2;

      const dy = Math.abs(o.y - playerY);
      const dx = Math.abs(obstacleX - playerX);

      // Collision condition
      if (dy < 60 && dx < laneW * 0.3) {
        if (!playerRef.current.invulnerable) {
          o.hit = true;
          setLives((l) => {
            const newLives = l - 1;

            if (newLives <= 0) {
              runningRef.current = false;
              setRunning(false);
              g.gameOver = true;
              setStatus("Game Over – Click Start");
            } else {
              playerRef.current.invulnerable = true;
              setTimeout(() => {
                playerRef.current.invulnerable = false;
                // *** FIX APPLIED HERE: The player stays in the current lane ***
                setStatus(
                  `In Lane ${
                    playerLaneRef.current + 1
                  }. Step left/right to move.`
                );
              }, PLAYER_HIT_COOLDOWN_MS);
              setStatus(
                `HIT! Invulnerable for 1s. Lives remaining: ${newLives}`
              );
            }
            return newLives;
          });
        }
      }
    });
  }

  /* ------------------------------------------------------
      DRAWING
  -------------------------------------------------------*/
  function draw() {
    const ctx = gameCanvasRef.current?.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // Background
    ctx.fillStyle = "#111920";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    const laneW = CANVAS_W / 3;

    // Draw lane lines (Moving Road Effect)
    ctx.strokeStyle = "#5a6878";
    ctx.lineWidth = 4;
    ctx.setLineDash([20, 40]);
    ctx.lineDashOffset = roadOffsetRef.current;

    ctx.beginPath();
    ctx.moveTo(laneW, 0);
    ctx.lineTo(laneW, CANVAS_H);
    ctx.moveTo(laneW * 2, 0);
    ctx.lineTo(laneW * 2, CANVAS_H);
    ctx.stroke();

    ctx.setLineDash([]);

    // Player
    const pLane = playerLaneRef.current;
    const px = pLane * laneW + laneW / 2;
    const py = CANVAS_H - 150;

    // Player Flash effect when invulnerable
    if (playerRef.current.invulnerable) {
      if (Math.floor(performance.now() / 50) % 2 === 0) {
        ctx.fillStyle = "#fff";
      } else {
        ctx.fillStyle = "#4ade80";
      }
    } else {
      ctx.fillStyle = "#4ade80";
    }

    ctx.beginPath();
    ctx.arc(px, py, 40, 0, Math.PI * 2);
    ctx.fill();

    // Obstacles
    ctx.fillStyle = "#f87171";
    gameRef.current.obstacles.forEach((o) => {
      const x = o.lane * laneW + laneW / 2;
      ctx.beginPath();
      ctx.rect(x - 35, o.y - 35, 70, 70);
      ctx.fill();
    });

    // Game Over Freeze Layer
    if (gameRef.current.gameOver) {
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      ctx.fillStyle = "#fff";
      ctx.font = "bold 60px Arial";
      ctx.textAlign = "center";
      ctx.fillText("GAME OVER", CANVAS_W / 2, CANVAS_H / 3);
    }
  }

  /* ------------------------------------------------------
      CAMERA PREVIEW 
  -------------------------------------------------------*/
  function drawCameraPreview() {
    if (!camCanvasRef.current || !videoRef.current) return;

    const ctx = camCanvasRef.current.getContext("2d");
    const CAM_THIRD = CAM_W / 3;

    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, CAM_W, CAM_H);

    if (videoRef.current.readyState >= 2) {
      ctx.save();
      ctx.scale(-1, 1);
      ctx.drawImage(videoRef.current, -CAM_W, 0, CAM_W, CAM_H);
      ctx.restore();
    }

    // 1. Draw Zone Lines
    ctx.strokeStyle = "rgba(255,255,255,0.7)";
    ctx.lineWidth = 2;
    ctx.setLineDash([]);

    ctx.beginPath();
    ctx.moveTo(CAM_THIRD, 0);
    ctx.lineTo(CAM_THIRD, CAM_H);
    ctx.moveTo(2 * CAM_THIRD, 0);
    ctx.lineTo(2 * CAM_THIRD, CAM_H);
    ctx.stroke();

    // 2. Highlight Zones based on motion count
    const { leftMotionCount, centerMotionCount, rightMotionCount, activeZone } =
      gestureRef.current;

    const leftIntensity = Math.min(1, leftMotionCount / MIN_ACTIVE_PIXELS);
    const centerIntensity = Math.min(1, centerMotionCount / MIN_ACTIVE_PIXELS);
    const rightIntensity = Math.min(1, rightMotionCount / MIN_ACTIVE_PIXELS);

    // Left Zone Highlight (Blue)
    ctx.fillStyle = `rgba(50, 150, 255, ${0.1 + leftIntensity * 0.2})`;
    ctx.fillRect(0, 0, CAM_THIRD, CAM_H);

    // Center Zone Highlight (White)
    ctx.fillStyle = `rgba(255, 255, 255, ${0.1 + centerIntensity * 0.2})`;
    ctx.fillRect(CAM_THIRD, 0, CAM_THIRD, CAM_H);

    // Right Zone Highlight (Green)
    ctx.fillStyle = `rgba(50, 255, 150, ${0.1 + rightIntensity * 0.2})`;
    ctx.fillRect(2 * CAM_THIRD, 0, CAM_THIRD, CAM_H);

    // 3. Highlight the Currently Active Lane (Stronger indicator)
    if (runningRef.current) {
      ctx.strokeStyle = "#4ade80"; // Bright green border
      ctx.lineWidth = 2;
      ctx.setLineDash([10, 5]);

      if (activeZone === 0) {
        ctx.strokeRect(2, 2, CAM_THIRD - 4, CAM_H - 4);
      } else if (activeZone === 1) {
        ctx.strokeRect(CAM_THIRD + 2, 2, CAM_THIRD - 4, CAM_H - 4);
      } else if (activeZone === 2) {
        ctx.strokeRect(2 * CAM_THIRD + 2, 2, CAM_THIRD - 4, CAM_H - 4);
      }
      ctx.setLineDash([]);
    }
  }

  /* ------------------------------------------------------
      GAME LOOP
  -------------------------------------------------------*/
  function gameLoop(t) {
    const dt = t - (gameLoop.last || t);
    gameLoop.last = t;

    detectZone(videoRef.current);
    drawCameraPreview();

    if (runningRef.current) {
      step(dt);
    }

    draw();
    if (runningRef.current || gameRef.current.gameOver) {
      rafRef.current = requestAnimationFrame(gameLoop);
    }
  }

  /* ------------------------------------------------------
      START / STOP
  -------------------------------------------------------*/
  async function startGame() {
    if (runningRef.current) return;

    setScore(0);
    setLives(3);
    setStatus("Starting...");
    playerLaneRef.current = 1; // Reset to center only on start
    gestureRef.current.activeZone = 1;
    playerRef.current.invulnerable = false;
    roadOffsetRef.current = 0;
    gameRef.current.obstacles = [];
    gameRef.current.time = 0;
    gameRef.current.currentScore = 0;
    gameRef.current.gameOver = false;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });

      streamRef.current = stream;
      videoRef.current = document.createElement("video");
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true;
      videoRef.current.play();

      runningRef.current = true;
      setRunning(true);
      gameLoop.last = performance.now();
      rafRef.current = requestAnimationFrame(gameLoop);

      setStatus("Move to the Left, Center, or Right zone to control the dot.");
    } catch (e) {
      setStatus(
        `Error starting camera: ${e.name}. Please allow camera access.`
      );
      console.error("Camera access failed:", e);
    }
  }

  function stopGame() {
    runningRef.current = false;
    setRunning(false);

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current)
      streamRef.current.getTracks().forEach((t) => t.stop());

    setStatus("Stopped – Click Start");
  }

  useEffect(() => {
    return () => stopGame();
  }, []);

  /* ------------------------------------------------------
      UI (Ant Design)
  -------------------------------------------------------*/
  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#333b47" }}>
      {/* CONTROL PANEL */}
      <div
        style={{
          width: 320,
          background: "#1f1f1f",
          padding: 16,
          color: "#fff",
          boxShadow: "4px 0 10px rgba(0,0,0,0.7)",
        }}
      >
        <Title level={3} style={{ color: "#bfe7ff", margin: "0 0 10px 0" }}>
          🏃 Step Runner
        </Title>
        <Text
          style={{
            fontSize: 14,
            fontWeight: "bold",
            color: "#bfe7ff",
            display: "block",
            marginBottom: 10,
          }}
        >
        Status: {status}
        </Text>

        <Divider style={{ borderColor: "rgba(255,255,255,0.2)" }} />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: 16,
          }}
        >
          <Button
            block
            size="large"
            type="primary"
            disabled={running}
            onClick={startGame}
            style={{
              background: running ? "#555" : "#1890ff",
              borderColor: running ? "#555" : "#1890ff",
            }}
          >
            Start Game
          </Button>

          <Button
            onClick={stopGame}
            disabled={!running}
            danger
            block
            size="large"
            style={{
              background: !running ? "#555" : "#ff4d4f",
              borderColor: !running ? "#555" : "#ff4d4f",
              color: "#fff",
            }}
          >
            Stop Game
          </Button>
        </div>

        <Divider style={{ borderColor: "rgba(255,255,255,0.2)" }} />

        <Title
          level={4}
          style={{ color: "#4ade80", margin: 0, marginBottom: 10 }}
        >
          Score: {Math.floor(score)}
        </Title>

        <Title level={4} style={{ color: "#ffc107", margin: 0 }}>
          Lives: {" "}
          {Array(3)
            .fill(0)
            .map((_, i) => (
              <span key={i} style={{ marginLeft: 5, fontSize: 20 }}>
                {i < lives ? "❤️" : "🖤"}
              </span>
            ))}
        </Title>

        <Divider style={{ borderColor: "rgba(255,255,255,0.2)" }} />

        <Text
          style={{
            color: "#999",
            fontSize: 11,
            fontWeight: "bold",
            display: "block",
          }}
        >
          Camera Preview:
        </Text>

        <Text style={{ color: "#888", fontSize: 12, display: "block", marginBottom: 8, }}>
          The active lane is highlighted with a green border.
        </Text>

        <canvas
          ref={camCanvasRef}
          width={CAM_W}
          height={CAM_H}
          style={{
            marginTop: 5,
            borderRadius: 8,
            border: running ? "2px solid #4ade80" : "2px solid #888",
            background: "#000",
            width: "100%",
          }}
        />
        {/* <div
          style={{
            marginTop: 8,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Tag color="#108ee9">Lane 1</Tag>
          <Tag color="#ffffff" style={{ color: "#000" }}>
            Lane 2
          </Tag>
          <Tag color="#87d068">Lane 3</Tag>
        </div> */}
      </div>

      {/* GAME CANVAS */}
      <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
        <canvas
          ref={gameCanvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          style={{
            marginTop: 20,
            borderRadius: 12,
            background: "#000",
            boxShadow: "0 0 25px rgba(0,0,0,1)",
          }}
        />
      </div>
    </div>
  );
}
